import { type Registry as Services, service } from '@ember/service';
import Model, { attr, belongsTo } from '@ember-data/model';
import { Type } from '@warp-drive/core-types/symbols';
import type { DayjsTransformReturn } from 'frontend-utils/transforms/dayjs';
import {
  CURRENCY_MAPPING,
  type SupportedCurrencies,
} from 'frontend-utils/utils/constants/currency-mapping-constants';
import type { Day, Levelbucket } from 'lumen/models';
import {
  DATE_AGGREGATION,
  MEALTYPE_ICONS,
} from 'lumen/utils/constants/general-constants';
import { DEMAND_INDEX_LEVEL_BUCKET_IDS } from 'lumen/utils/constants/market-insight-constants';
import convertIndexToMarketInsightDemandLevel from 'lumen/utils/market-insight/index-to-color-level';

export default class Rateindex extends Model {
  [Type] = 'rateindex' as const;

  @belongsTo('day', { async: false, inverse: 'rateIndexes' })
  declare day: Day;

  @attr('number') declare benchmark: number;
  @attr('number') declare benchmarkDow: number;
  @attr('string') declare benchmarkMessage: string;
  @attr('boolean') declare cancellation: boolean;
  @attr('string') declare currency: SupportedCurrencies;
  @attr('string') declare dateAggregation: string;
  @attr('dayjs') declare extractDate: DayjsTransformReturn;
  @attr('boolean') declare isLatest: boolean;
  @attr('number') declare mealtypeIncluded: keyof typeof MEALTYPE_ICONS;
  @attr('string') declare message: string;
  @attr('number') declare ourHotelId: number;
  @attr('number') declare rate: number;
  @attr('string') declare roomName: string;
  @attr('number') declare usualRate: number;
  @attr('number') declare usualRateDow: number;
  @attr('number') declare value: number;
  @attr('number') declare valueDow: number;

  @service declare intl: Services['intl'];

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get canShowFormattedCancellableText() {
    return !this.isRateIndexSoldOut;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevelColorScaleNumber() {
    let rateIndexLevelNumber = null;

    if (this.hasCorrectHotelDemandLevelColorScaleNumber) {
      // 05/03/21: we used to do a peekrecord for the hotel specific level bucket here.
      // We've decided to change that to the demand index level bucket to avoid confusion in the UI - Slack discussion: https://otainsight.slack.com/archives/GGLM7LJ4W/p1583337219019200
      const rateIndexLevelBucket = this.store.peekRecord<Levelbucket>(
        'levelbucket',
        DEMAND_INDEX_LEVEL_BUCKET_IDS.YEARLY,
      );

      rateIndexLevelNumber = convertIndexToMarketInsightDemandLevel(
        this.value,
        rateIndexLevelBucket,
      );
    }

    return rateIndexLevelNumber;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get demandLevelColorScaleNumberDow() {
    let rateIndexLevelNumber = null;

    if (this.hasCorrectHotelDemandLevelColorScaleNumber) {
      // 05/03/21: we used to do a peekrecord for the hotel specific level bucket here.
      // We've decided to change that to the demand index level bucket to avoid confusion in the UI - Slack discussion: https://otainsight.slack.com/archives/GGLM7LJ4W/p1583337219019200
      const rateIndexLevelBucket = this.store.peekRecord<Levelbucket>(
        'levelbucket',
        DEMAND_INDEX_LEVEL_BUCKET_IDS.DOW,
      );

      rateIndexLevelNumber = convertIndexToMarketInsightDemandLevel(
        this.valueDow,
        rateIndexLevelBucket,
      );
    }

    return rateIndexLevelNumber;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedCancellableIcon() {
    if (!this.canShowFormattedCancellableText) {
      return '';
    }

    return this.cancellation ? 'rates-best-flex' : 'rates-lowest';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedCancellableText() {
    if (!this.canShowFormattedCancellableText) {
      return '';
    }

    return this.cancellation
      ? this.intl.t('rates.best_flex')
      : this.intl.t('rates.lowest');
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedMealtypeIncludedIcon() {
    if (this.isRateIndexSoldOut) {
      return undefined;
    }

    return MEALTYPE_ICONS[this.mealtypeIncluded] || undefined;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRate() {
    if (this.isMissing) {
      return this.intl.t('general.missing');
    }

    if (this.isRateIndexSoldOut) {
      return `${this.intl.t('general.soldout')}`;
    }

    let formattedRateValue = '';

    if (CURRENCY_MAPPING && CURRENCY_MAPPING[this.currency]) {
      formattedRateValue = `${CURRENCY_MAPPING[this.currency]} ${Math.round(this.rate)}`;
    } else {
      formattedRateValue = `${this.currency} ${Math.round(this.rate)}`;
    }

    if (this.isRatesSoldOut) {
      return `${this.intl.t('general.soldout')} (${formattedRateValue})`;
    }

    return formattedRateValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedRoomNameAndCancellableText() {
    let formattedRoomNameAndCancellableText = `${this.roomName}`;

    if (this.formattedCancellableText) {
      formattedRoomNameAndCancellableText += ` - ${this.formattedCancellableText}`;
    }

    return formattedRoomNameAndCancellableText;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedUsualRate() {
    if (this.isMissing || this.isRateIndexSoldOut) {
      return '--';
    }

    let formattedRateValue = '';
    if (CURRENCY_MAPPING && CURRENCY_MAPPING[this.currency]) {
      formattedRateValue = `${CURRENCY_MAPPING[this.currency]} ${Math.round(this.usualRate)}`;
    } else {
      formattedRateValue = `${this.currency} ${Math.round(this.usualRate)}`;
    }

    return formattedRateValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get formattedUsualRateDow() {
    if (!this.hasUsualRateDowValue) {
      return '--';
    }

    let formattedRateValue = '';
    if (CURRENCY_MAPPING && CURRENCY_MAPPING[this.currency]) {
      formattedRateValue = `${CURRENCY_MAPPING[this.currency]} ${Math.round(this.usualRateDow)}`;
    } else {
      formattedRateValue = `${this.currency} ${Math.round(this.usualRateDow)}`;
    }

    return formattedRateValue;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasCorrectHotelDemandLevelColorScaleNumber() {
    // Basically same checks as hasCorrectRateIndexValue, but we allow rates.soldout here
    // We still want to be able to show the demand level color in the "My price" list. It's still useful to see which strategy competitors had before they went soldout
    return (
      !this.isMissing && !this.isRateIndexSoldOut && !this.isRateIndexMissing
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasCorrectRateIndexValue() {
    // In these cases we don't want to show the percentages (e.g. in the tooltips)
    return (
      !this.isMissing &&
      !this.isRateIndexSoldOut &&
      !this.isRatesSoldOut &&
      !this.isRateIndexMissing
    );
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get hasUsualRateDowValue() {
    return !(this.isMissing || this.isRateIndexSoldOut);
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isBenchmarkMissing() {
    return this.message === 'rateindex.benchmark_missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isMissing() {
    return this.message === 'general.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRateIndexMissing() {
    // The rate index value itself could not be calculated, but the rate information is available
    // -> We can't display a price strategy, but we can show a price in app
    return this.message === 'rateindex.missing';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRateIndexSoldOut() {
    // Always sold out / no data
    return this.message === 'rateindex.soldout';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get isRatesSoldOut() {
    // We have rate index data, but the hotel is currently sold out
    return this.message === 'rates.soldout';
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get outliersCount() {
    let opportunitiesCount = 0;

    if (this.dateAggregation === DATE_AGGREGATION.WEEK) {
      if (this.day) {
        // Start with the day we got as a param (should be a monday)
        opportunitiesCount = this.day.outliers.length;
        const dayMoment = this.day.date;
        // Then fetch the other days (until sunday)
        for (let daysToAdd = 1; daysToAdd < 7; daysToAdd += 1) {
          const dayInSameWeek = this.store.peekRecord<Day>(
            'day',
            dayMoment.add(daysToAdd, 'day').format('YYYY-MM-DD'),
          );
          // And add their opportunities
          opportunitiesCount +=
            (dayInSameWeek?.outliers?.length as number) ?? 0;
        }
      }
    } else {
      const { outliers } = this.day;
      opportunitiesCount = outliers.length;
    }
    return opportunitiesCount;
  }

  /**
   * Returns if the price strategy is very low/low/.../very high
   */
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get rateLevelText() {
    if (this.hasCorrectRateIndexValue) {
      return this.intl.t(
        `market_insight.rate_level.level${this.demandLevelColorScaleNumber}`,
      );
    }

    return null;
  }
}
